import React from "react";

import { Badge as MuiBadge } from "@material-ui/core";
import { useSelector } from "react-redux";
import styled from "styled-components/macro";

import { Tooltip } from "components/Form/FormikControls/Tooltip";
import { Row } from "components/Layout";

import { AdvancedDraftingValidationStates } from "constants/draftingAttributes";
import {
  ExportSite,
  ExportSiteHeaders,
  ExportSiteIcons,
} from "constants/exportSites";

import { advancedDraftingValidation } from "lib/validation/advancedDraftingValidation";
import { ErrorDict } from "lib/validation/doValidate";

import {
  getConsignments,
  getIntegrationErrorsByAuctionPenId,
  getIntegrationErrorsOrStatusBySaleLotId,
  getNotAngusVerifiedCountByPenId,
  getRounds,
  selectCurrentDeploymentSales,
} from "selectors";

import AngusAustraliaLogo from "img/AngusAustralia";

type ExportSiteValidationStatus = Record<string, string | ErrorDict>;

const IndicatorWarningBadge = styled(MuiBadge)`
  margin-top: 30px;
  margin-right: 30px;
`;

interface WarningBadgeProps {
  children: React.ReactNode;
  badgeContent: number | null;
  color?: string;
}

const WarningBadge = ({
  children,
  badgeContent,
  color,
}: WarningBadgeProps): React.JSX.Element => {
  return (
    <IndicatorWarningBadge badgeContent={badgeContent} color={color || "error"}>
      {children}
    </IndicatorWarningBadge>
  );
};

const AuctionPenIntegrationIcon = React.memo(
  ({ auctionPenId }: { auctionPenId: string }): React.JSX.Element | null => {
    const exportSiteStatus: ExportSiteValidationStatus = useSelector(
      getIntegrationErrorsByAuctionPenId(auctionPenId),
    );

    const notAngusVerifiedCount: number | null = useSelector(
      getNotAngusVerifiedCountByPenId(auctionPenId),
    );

    if (
      Object.keys(exportSiteStatus).length === 0 &&
      notAngusVerifiedCount === null
    ) {
      return null;
    } else {
      return (
        <Row>
          {typeof notAngusVerifiedCount === "number" &&
          notAngusVerifiedCount === 0 ? (
            <AngusAustraliaLogo />
          ) : null}
          {Object.keys(exportSiteStatus).map(exportSite => {
            const Icon =
              ExportSiteIcons[exportSite as keyof typeof ExportSiteIcons];
            if (Icon) {
              const { disabledCount, status } = exportSiteStatus[
                exportSite
              ] as unknown as { disabledCount: number; status: string };
              return (
                <WarningBadge key={exportSite} badgeContent={disabledCount}>
                  <Icon
                    color={status}
                    disabled={
                      status === AdvancedDraftingValidationStates.DISABLED
                    }
                  />
                </WarningBadge>
              );
            } else {
              return null;
            }
          })}
        </Row>
      );
    }
  },
);

interface ExportSiteToolTipProps {
  statusOrErrors: string | ErrorDict;
  exportSite: string;
}

function ExportSiteToolTip({
  statusOrErrors,
  exportSite,
}: ExportSiteToolTipProps): React.JSX.Element | string {
  if (typeof statusOrErrors === "object") {
    return (
      <span>
        {Object.values(statusOrErrors).map((error, idx) => (
          <li key={`error-${idx}`}>{error}</li>
        ))}
      </span>
    );
  } else if (statusOrErrors === AdvancedDraftingValidationStates.DISABLED) {
    return `${ExportSiteHeaders[exportSite as ExportSite]} disabled`;
  }
  return `${ExportSiteHeaders[exportSite as ExportSite]} compliant`;
}

const SaleLotIntegrationIcon = React.memo(
  ({
    exportSiteErrors,
  }: {
    exportSiteErrors: ExportSiteValidationStatus;
  }): React.JSX.Element | null => {
    if (Object.keys(exportSiteErrors).length === 0) {
      return null;
    } else {
      return (
        <Row>
          {Object.keys(exportSiteErrors).map(exportSite => {
            const Icon =
              ExportSiteIcons[exportSite as keyof typeof ExportSiteIcons];
            if (Icon) {
              const statusOrErrors = exportSiteErrors[exportSite];
              const hasErrors = typeof statusOrErrors === "object";

              const title = (
                <ExportSiteToolTip
                  statusOrErrors={statusOrErrors}
                  exportSite={exportSite}
                />
              );
              return (
                <Tooltip key={exportSite} title={title}>
                  <Icon
                    color={hasErrors ? "warning" : statusOrErrors}
                    disabled={
                      statusOrErrors ===
                      AdvancedDraftingValidationStates.DISABLED
                    }
                  />
                </Tooltip>
              );
            } else {
              return null;
            }
          })}
        </Row>
      );
    }
  },
);

const SaleLotByIdIntegrationIcon = React.memo(
  ({ saleLotId }: { saleLotId: string }): React.JSX.Element => {
    const exportSiteErrors: ExportSiteValidationStatus = useSelector(
      getIntegrationErrorsOrStatusBySaleLotId(saleLotId),
    );
    return <SaleLotIntegrationIcon exportSiteErrors={exportSiteErrors} />;
  },
);

const SaleLotByValuesIntegrationIcon = React.memo(
  ({
    saleLotValues,
  }: {
    saleLotValues: Partial<SaleLot>;
  }): React.JSX.Element => {
    const currentDeploymentSales = useSelector(selectCurrentDeploymentSales);
    const consignments = useSelector(getConsignments);
    const rounds = useSelector(getRounds);

    const exportSiteErrors = advancedDraftingValidation(
      saleLotValues as SaleLot,
      currentDeploymentSales,
      consignments,
      rounds,
    );

    return <SaleLotIntegrationIcon exportSiteErrors={exportSiteErrors} />;
  },
);

interface IntegrationIconProps {
  auctionPenId?: string;
  saleLotId?: string;
  saleLotValues?: Partial<SaleLot>;
}

export const IntegrationIcon = React.memo(
  ({
    auctionPenId,
    saleLotId,
    saleLotValues,
  }: IntegrationIconProps): React.JSX.Element | null => {
    if (auctionPenId) {
      return <AuctionPenIntegrationIcon auctionPenId={auctionPenId} />;
    }
    if (saleLotId) {
      return <SaleLotByIdIntegrationIcon saleLotId={saleLotId} />;
    }
    if (saleLotValues) {
      return <SaleLotByValuesIntegrationIcon saleLotValues={saleLotValues} />;
    }
    return null;
  },
);

export default IntegrationIcon;
